import { Link } from "react-router-dom";
import { Lock, Sparkles, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { TIER_LABELS, type PlanTier } from "@/lib/tiers";

interface FeatureLockedPageProps {
  icon?: React.ComponentType<{ className?: string }>;
  feature: string;
  requiredTier: PlanTier;
  description: string;
  benefits?: string[];
}

/**
 * Full-page lock for features the current tier doesn't include.
 * Use as the entire page body (e.g. Analytics, Brand Kit) instead of an inline upsell.
 */
export function FeatureLockedPage({
  icon: Icon = Lock,
  feature,
  requiredTier,
  description,
  benefits = [],
}: FeatureLockedPageProps) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] py-8">
      <Card className="max-w-lg w-full border-primary/20">
        <CardHeader className="text-center pb-4">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Icon className="w-7 h-7 text-primary" />
          </div>
          <CardTitle className="text-2xl">{feature}</CardTitle>
          <CardDescription className="text-base mt-2">
            Available on the {TIER_LABELS[requiredTier]} plan
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground text-center">{description}</p>

          {benefits.length > 0 && (
            <ul className="space-y-2">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Link to="/pricing" className="flex-1">
              <Button className="w-full">
                <Sparkles className="w-4 h-4 mr-2" />
                View plans
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/dashboard" className="flex-1">
              <Button variant="outline" className="w-full">Back to dashboard</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
